import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';

function DashboardPage() {
    const navigate = useNavigate();

    const [user, setUser] = useState({
        name: 'Loading...',
        email: '',
        phone: '',
        kycStatus: 'pending'
    });

    const [stats, setStats] = useState({
        creditScore: 750,
        availableCredit: '5,00,000',
        activeLoans: 0,
        upcomingPayment: '0'
    });

    const [activities, setActivities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchDashboardData = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (session && session.userId) {
            setIsLoading(true);
            fetch(`${API_URL}/api/dashboard/all-data/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        setUser({
                            id: data.user.id || session.userId,
                            name: data.user.name,
                            email: data.user.email,
                            phone: data.user.phone,
                            kycStatus: data.user.kycStatus || 'pending'
                        });
                        setStats(data.stats);
                        setActivities(data.activities);
                    }
                })
                .catch(err => console.error('Error fetching dashboard optimized data:', err))
                .finally(() => setIsLoading(false));
        }
    }, [API_URL]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const getActivityIcon = (type) => {
        switch (type.toLowerCase()) {
            case 'payment': return '💳';
            case 'document': return '📄';
            case 'credit': return '📊';
            case 'login': return '🔐';
            default: return 'ℹ️';
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-page-new" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <div className="loading-spinner" style={{ border: '4px solid rgba(247, 189, 32, 0.1)', borderTop: '4px solid #f7bd20', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                    <p style={{ color: '#666' }}>Optimizing your dashboard...</p>
                    <style>{`
                        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    `}</style>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page-new">
            {/* Elegant Welcome Section */}
            <div className="welcome-section" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1>Welcome back, {user.name.split(' ')[0]} 👋</h1>
                    <p className="welcome-subtitle">Here is your financial overview </p>
                </div>
                {user.kycStatus === 'completed' && (
                    <div className="stat-badge" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '6px 14px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.85rem' }}>
                        ✓ KYC VERIFIED
                    </div>
                )}
            </div>

            {/* Core Stats Cards - Clean Grid */}
            <div className="stats-cards">
                <div className="stat-card-new primary-card">
                    <div className="stat-header">
                        <span className="stat-label">Credit Score</span>
                        <span className="stat-icon">📈</span>
                    </div>
                    <div className="stat-value-large">{stats.creditScore}</div>
                    <div className="stat-footer">
                        <span className="stat-text" style={{ color: 'rgba(0,0,0,0.6)', fontWeight: '600' }}>Updated today</span>
                    </div>
                    <div className="score-bar">
                        <div className="score-fill" style={{ width: `${(stats.creditScore / 900) * 100}%` }}></div>
                    </div>
                </div>

                <div className="stat-card-new">
                    <div className="stat-header">
                        <span className="stat-label">Available Credit</span>
                        <span className="stat-icon">💰</span>
                    </div>
                    <div className="stat-value-large">₹{stats.availableCredit}</div>
                    <div className="stat-footer">
                        <span className="stat-badge" style={{ background: '#fef3c7', color: '#92400e' }}>Ready to Disburse</span>
                    </div>
                </div>

                <div className="stat-card-new">
                    <div className="stat-header">
                        <span className="stat-label">Active Loans</span>
                        <span className="stat-icon">📋</span>
                    </div>
                    <div className="stat-value-large">{stats.activeLoans}</div>
                    <div className="stat-footer">
                        <span className="stat-text">All accounts in good standing</span>
                    </div>
                </div>

                <div className="stat-card-new">
                    <div className="stat-header">
                        <span className="stat-label">Next Payment</span>
                        <span className="stat-icon">💳</span>
                    </div>
                    <div className="stat-value-large">₹{stats.upcomingPayment}</div>
                    <div className="stat-footer">
                        <span className="stat-text">Due in 5 days</span>
                    </div>
                </div>
            </div>

            {/* Quick Actions - Clean Icon Tiles */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Quick Actions</h2>
                </div>
                <div className="quick-actions-grid">
                    <button className="action-card" onClick={() => navigate('/dashboard/loans')}>
                        <div className="action-icon">💰</div>
                        <span>My Loans</span>
                    </button>
                    <button className="action-card" onClick={() => navigate('/dashboard/payments')}>
                        <div className="action-icon">💳</div>
                        <span>Payments</span>
                    </button>
                    <button className="action-card" onClick={() => navigate('/dashboard/documents')}>
                        <div className="action-icon">📄</div>
                        <span>Documents</span>
                    </button>
                    <button className="action-card" onClick={() => navigate('/dashboard/profile')}>
                        <div className="action-icon">👤</div>
                        <span>Profile</span>
                    </button>
                    <button className="action-card" onClick={() => navigate('/dashboard/support')}>
                        <div className="action-icon">💬</div>
                        <span>Support</span>
                    </button>
                    <button className="action-card" onClick={() => navigate('/dashboard/kyc')}>
                        <div className="action-icon">🆔</div>
                        <span>KYC</span>
                    </button>
                </div>
            </div>

            {/* Recent Activity - Minimalist List */}
            <div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Recent Activity</h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {activities.length > 0 ? (
                        activities.slice(0, 5).map((act) => (
                            <div key={act.id} className="activity-item-new" style={{ padding: '1.25rem' }}>
                                <div className="activity-content">
                                    <div style={{ fontSize: '1.5rem' }}>{getActivityIcon(act.activity_type)}</div>
                                    <div className="activity-info">
                                        <h4>{act.activity_type}</h4>
                                        <p>{act.description}</p>
                                    </div>
                                </div>
                                <span className="activity-time">
                                    {new Date(act.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                            Your recent activities will appear here.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DashboardPage;
